import React, { Component } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';

import {
  FormattedMessage,
  injectIntl,
  intlShape,
} from '../../../util/reactIntl';
import { ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { isUploadImageOverLimitError } from '../../../util/errors';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import {
  Form,
  Avatar,
  Button,
  ImageFromFile,
  IconSpinner,
  FieldTextInput,
  FieldSelect,
  H4,
  CustomExtendedDataField,
} from '../../../components';

import css from './ProfileSettingsForm.module.css';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset
const MAX_LENGTH = 500;
const MAX_MEMBERS = 10;
const AGE_RANGE_OPTIONS = ['Adult', 'Teen', 'Child', 'Baby', 'Pet'];
const FAMILY_NAME_MAX_LENGTH = 50;
const DisplayNameMaybe = props => {
  const { userTypeConfig, intl, initialValues } = props;

  const isDisabled = userTypeConfig?.defaultUserFields?.displayName === false;
  if (isDisabled) {
    return null;
  }

  const { required } = userTypeConfig?.displayNameSettings || {};
  const isRequired = required === true;

  const validateMaybe = isRequired
    ? {
      validate: validators.required(
        intl.formatMessage({
          id: 'ProfileSettingsForm.displayNameRequired',
        })
      ),
    }
    : {};

  return (
    <div className={css.sectionContainer}>
      <H4 as="h2" className={css.sectionTitle}>
        <FormattedMessage id="ProfileSettingsForm.displayNameHeading" />
      </H4>
      <FieldTextInput
        className={css.row}
        type="text"
        id="displayName"
        name="displayName"
        initialValue={initialValues?.displayName}
        label={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNameLabel',
        })}
        placeholder={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNamePlaceholder',
        })}
        {...validateMaybe}
      />
      <p className={css.extraInfo}>
        <FormattedMessage id="ProfileSettingsForm.displayNameInfo" />
      </p>
    </div>
  );
};

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    // DOM nodes of the rendered family member rows, used to work out which row
    // the pointer is over while dragging, and to restore focus after a
    // keyboard-driven move.
    this.memberNodes = [];
    this.memberHandles = [];
    this.state = {
      uploadDelay: false,
      isDisplayNameEdited: false,
      isFamilyNameEdited: false,
      showSaveConfirmation: false,
      // Index of the member row being dragged and of the row it would be
      // dropped on. Both null when no drag is in progress.
      memberDragIndex: null,
      memberDropIndex: null,
    };
  }

  // Index of the member row under the given viewport y coordinate. Falls back
  // to the closest row so that dragging past either end of the list still
  // resolves to the first/last position.
  memberIndexAtY(clientY, count) {
    let closestIndex = null;
    let closestDistance = Infinity;

    for (let i = 0; i < count; i++) {
      const node = this.memberNodes[i];
      if (!node) {
        continue;
      }
      const rect = node.getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        return i;
      }
      const distance =
        clientY < rect.top ? rect.top - clientY : clientY - rect.bottom;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }
    return closestIndex;
  }

  handleMemberDragStart(e, index) {
    // Only start a drag for the primary pointer/button.
    if (typeof e.button === 'number' && e.button > 0) {
      return;
    }
    // Prevents text selection and touch scrolling while dragging.
    e.preventDefault();
    const handle = e.currentTarget;
    if (handle.setPointerCapture) {
      try {
        handle.setPointerCapture(e.pointerId);
      } catch (err) {
        // Pointer capture is a progressive enhancement: without it the drag
        // still works as long as the pointer stays over the handle.
      }
    }
    this.setState({ memberDragIndex: index, memberDropIndex: index });
  }

  handleMemberDragMove(e, count) {
    if (this.state.memberDragIndex === null) {
      return;
    }
    const memberDropIndex = this.memberIndexAtY(e.clientY, count);
    if (
      memberDropIndex !== null &&
      memberDropIndex !== this.state.memberDropIndex
    ) {
      this.setState({ memberDropIndex });
    }
  }

  handleMemberDragEnd(fields) {
    const { memberDragIndex, memberDropIndex } = this.state;
    if (
      memberDragIndex !== null &&
      memberDropIndex !== null &&
      memberDragIndex !== memberDropIndex
    ) {
      fields.move(memberDragIndex, memberDropIndex);
    }
    this.setState({ memberDragIndex: null, memberDropIndex: null });
  }

  handleMemberDragCancel() {
    this.setState({ memberDragIndex: null, memberDropIndex: null });
  }

  // Keyboard equivalent of dragging: arrow up/down moves the focused member
  // one position and keeps focus on the same member.
  handleMemberKeyDown(e, index, fields) {
    const isUp = e.key === 'ArrowUp';
    const isDown = e.key === 'ArrowDown';
    if (!isUp && !isDown) {
      return;
    }
    const targetIndex = isUp ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) {
      return;
    }
    e.preventDefault();
    fields.move(index, targetIndex);
    window.requestAnimationFrame(() => {
      const handle = this.memberHandles[targetIndex];
      if (handle) {
        handle.focus();
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }

    // Show the checkmark once a submit finishes successfully. We track this
    // with local state instead of comparing form values to a submitted-values
    // snapshot, since re-fetched entities (e.g. profileImage) get new object
    // references from the server response and can fail a deep-equal check
    // even though nothing actually differs.
    if (prevProps.updateInProgress && !this.props.updateInProgress) {
      this.setState({ showSaveConfirmation: !this.props.updateProfileError });
    } else if (!prevProps.updateInProgress && this.props.updateInProgress) {
      this.setState({ showSaveConfirmation: false });
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        mutators={{ ...arrayMutators }}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            formId,
            marketplaceName,
            values,
            userFields,
            userTypeConfig,
            initialValues,
          } = fieldRenderProps;
          const user = ensureCurrentUser(currentUser);

          // First name
          const firstNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameLabel',
          });
          const firstNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNamePlaceholder',
          });
          const firstNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameRequired',
          });
          const firstNameRequired = validators.required(
            firstNameRequiredMessage
          );

          // Last name
          const lastNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameLabel',
          });
          const lastNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNamePlaceholder',
          });
          const lastNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameRequired',
          });
          const lastNameRequired = validators.required(lastNameRequiredMessage);
          const displayNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.displayNameLabel',
          });
          const displayNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.displayNamePlaceholder',
          });

          // Bio
          const bioLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.bioLabel',
          });
          const bioPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.bioPlaceholder',
          });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({
            [css.avatarUploadError]: hasUploadError,
          });
          const transientUserProfileImage =
            profileImage.uploadedImage || user.profileImage;
          const transientUser = {
            ...user,
            profileImage: transientUserProfileImage,
          };

          // Ensure that file exists if imageFromFile is used
          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload =
            profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectWidth={1}
                aspectHeight={1}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          // Avatar is rendered in hidden during the upload delay
          // Upload delay smoothes image change process:
          // responsive img has time to load srcset stuff before it is shown to user.
          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="(max-width: 767px) 96px, 240px"
                user={transientUser}
                disableProfileLink
              />
            ) : null;

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <div className={css.avatarContainer}>
                {imageFromFile}
                {avatarComponent}
                {/* <div className={css.changeAvatar}>
                  <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                </div> */}
              </div>
            ) : (
              <div className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;

          const whyJoinRequiredMessage = intl.formatMessage({
            id: 'InvitationForm.whyJoinRequired',
          });

          const whyJoinRequired = validators.required(whyJoinRequiredMessage);
          const maxLengthMessagetype = intl.formatMessage(
            { id: 'ProfileSettingsForm.maxhometypeLength' },
            {
              maxLength: MAX_LENGTH,
            }
          );
          const maxLength500Message = validators.maxLength(
            maxLengthMessagetype,
            MAX_LENGTH
          );

          const familyNameMaxLengthMessage = intl.formatMessage(
            { id: 'ProfileSettingsForm.maxFamilyNameLength' },
            { maxLength: FAMILY_NAME_MAX_LENGTH }
          );
          const familyNameMaxLength = validators.maxLength(
            familyNameMaxLengthMessage,
            FAMILY_NAME_MAX_LENGTH
          );

          const memberNameMaxLengthMessage = intl.formatMessage(
            { id: 'ProfileSettingsForm.maxMemberNameLength' },
            { maxLength: 20 }
          );
          const memberNameMaxLength = validators.maxLength(
            memberNameMaxLengthMessage,
            20
          );

          const memberRelationshipMaxLengthMessage = intl.formatMessage(
            { id: 'ProfileSettingsForm.maxMemberRelationshipLength' },
            { maxLength: 20 }
          );
          const memberRelationshipMaxLength = validators.maxLength(
            memberRelationshipMaxLengthMessage,
            20
          );

          const submitDisabled =
            invalid ||
            pristine ||
            this.state.showSaveConfirmation ||
            uploadInProgress ||
            submitInProgress;

          const userFieldProps = getPropsForCustomUserFieldInputs(
            userFields,
            intl,
            userTypeConfig?.userType,
            false
          );

          return (
            <Form className={classes} onSubmit={handleSubmit}>
              <div className={css.sectionContainer}>
                <H4 as="h2" className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourProfilePicture" />
                </H4>
                <Field
                  accept={ACCEPT_IMAGES}
                  id="profileImage"
                  name="profileImage"
                  label={chooseAvatarLabel}
                  type="file"
                  form={null}
                  uploadImageError={uploadImageError}
                  disabled={uploadInProgress}
                >
                  {fieldProps => {
                    const {
                      accept,
                      id,
                      input,
                      label,
                      disabled,
                      uploadImageError,
                    } = fieldProps;
                    const { name, type } = input;
                    const onChange = e => {
                      const file = e.target.files[0];
                      form.change(`profileImage`, file);
                      form.blur(`profileImage`);
                      if (file != null) {
                        const tempId = `${file.name}_${Date.now()}`;
                        onImageUpload({ id: tempId, file });
                      }
                    };

                    let error = null;

                    if (isUploadImageOverLimitError(uploadImageError)) {
                      error = (
                        <div className={css.error}>
                          <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                        </div>
                      );
                    } else if (uploadImageError) {
                      error = (
                        <div className={css.error}>
                          <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                        </div>
                      );
                    }

                    return (
                      <div className={css.uploadAvatarWrapper}>
                        <label className={css.label} htmlFor={id}>
                          {label}
                        </label>
                        <input
                          accept={accept}
                          id={id}
                          name={name}
                          className={css.uploadAvatarInput}
                          disabled={disabled}
                          onChange={onChange}
                          type={type}
                        />
                        {error}
                      </div>
                    );
                  }}
                </Field>
                {/* <div className={css.tip}>
                  <FormattedMessage id="ProfileSettingsForm.tip" />
                </div> */}
                {/* <div className={css.fileInfo}>
                  <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                </div> */}
              </div>
              <div className={css.sectionContainer}>
                <H4 as="h2" className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourNameAccountOwner" />
                </H4>
                <div className={css.nameContainer}>
                  <FieldTextInput
                    className={css.firstName}
                    type="text"
                    id="firstName"
                    name="firstName"
                    label={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    validate={firstNameRequired}
                  />
                  <FieldTextInput
                    className={css.lastName}
                    type="text"
                    id="lastName"
                    name="lastName"
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    validate={lastNameRequired}
                    onChange={e => {
                      const nextLastName = e.target.value || '';
                      // Auto-update family name if it hasn't been manually edited
                      if (!this.state.isFamilyNameEdited) {
                        form.change('familyName', nextLastName);
                      }
                      // Auto-update display name if it hasn't been manually edited
                      if (!this.state.isDisplayNameEdited) {
                        form.change('displayName', nextLastName);
                      }
                    }}
                  />
                </div>
              </div>

              <div className={css.sectionContainer}>
                <label className={css.labelInput}>
                  <FormattedMessage id="ProfileSettingsForm.yourHometown" />
                </label>
                <FieldTextInput
                  className={css.row}
                  type="text"
                  id="hometown"
                  name="hometown"
                  placeholder={intl.formatMessage({
                    id: 'ProfileSettingsForm.hometownPlaceholder',
                  })}
                />
              </div>

              <div className={css.sectionContainer}>
                <H4 as="h2" className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourFamilyName" />
                </H4>
                <div className={css.familyNameWrapper}>
                  <p className={css.familyNamePrefix}>
                    <FormattedMessage id="ProfileSettingsForm.familyNamePrefix" />{' '}
                    <span className={css.displayName}>
                      {values?.familyName || values?.lastName || '          '}
                    </span>{' '}
                    <FormattedMessage id="ProfileSettingsForm.familyNameSuffix" />
                  </p>
                </div>
                <div>
                  <FieldTextInput
                    className={css.familyNameInput}
                    type="text"
                    id="familyName"
                    name="familyName"
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.familyNamePlaceholder',
                    })}
                    validate={familyNameMaxLength}
                  />
                </div>
              </div>
              <div className={classNames(css.sectionContainer)}>
                <H4 as="h2" className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.aboutYourFamily" />
                </H4>
                <p className={css.bioSubtitle}>
                  <FormattedMessage id="ProfileSettingsForm.aboutYourFamilySubtitle" />
                </p>
                <FieldTextInput
                  type="textarea"
                  id="bio"
                  name="bio"
                  placeholder={bioPlaceholder}
                  className={css.textarea}
                  validate={validators.composeValidators(whyJoinRequired)}
                />
              </div>
              <div className={css.spacer}></div>
              <div className={css.sectionContainer}>
                <H4 as="h2" className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.familyMembersHeading" />
                </H4>
                <FieldArray name="householdMembers">
                  {({ fields }) => (
                    <div className={css.membersContainer}>
                      <div className={css.memberInputRow}>
                        <FieldTextInput
                          className={css.memberFirstNameInput}
                          type="text"
                          id="newMemberFirstName"
                          name="newMemberFirstName"
                          // When nobody has been added yet, seed the first
                          // entry with the account creator's first name so the
                          // default account owner is the right person. It is a
                          // field-level initial value, so the field stays fully
                          // editable (and clearable) and the form stays
                          // pristine until something actually changes.
                          initialValue={
                            initialValues?.householdMembers?.length
                              ? undefined
                              : initialValues?.firstName
                          }
                          label={intl.formatMessage({
                            id: 'ProfileSettingsForm.firstNameLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'ProfileSettingsForm.firstNamePlaceholder',
                          })}
                          validate={memberNameMaxLength}
                        />
                        <FieldSelect
                          className={css.memberAgeInput}
                          selectClassName={
                            values.newMemberAgeRange
                              ? undefined
                              : css.selectPlaceholder
                          }
                          id="newMemberAgeRange"
                          name="newMemberAgeRange"
                          label={intl.formatMessage({
                            id: 'ProfileSettingsForm.ageRangeLabel',
                          })}
                        >
                          <option value="">
                            {intl.formatMessage({
                              id: 'ProfileSettingsForm.ageRangePlaceholder',
                            })}
                          </option>
                          {AGE_RANGE_OPTIONS.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </FieldSelect>
                        <FieldTextInput
                          className={css.memberRelationshipInput}
                          type="text"
                          id="newMemberRelationship"
                          name="newMemberRelationship"
                          label={intl.formatMessage({
                            id: 'ProfileSettingsForm.relationshipLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id:
                              values.newMemberAgeRange === 'Pet'
                                ? 'ProfileSettingsForm.relationshipPetPlaceholder'
                                : 'ProfileSettingsForm.relationshipFamilyPlaceholder',
                          })}
                          validate={memberRelationshipMaxLength}
                        />
                        <Button
                          type="button"
                          className={css.addButtonCircle}
                          disabled={
                            fields.length >= MAX_MEMBERS ||
                            !values.newMemberFirstName ||
                            !values.newMemberAgeRange ||
                            !values.newMemberRelationship ||
                            (values.newMemberFirstName && values.newMemberFirstName.length > 20) ||
                            (values.newMemberRelationship && values.newMemberRelationship.length > 20)
                          }
                          onClick={() => {
                            const firstName = values.newMemberFirstName;
                            const ageRange = values.newMemberAgeRange;
                            const relationship = values.newMemberRelationship;

                            if (
                              firstName &&
                              ageRange &&
                              relationship &&
                              fields.length < MAX_MEMBERS &&
                              firstName.length <= 20 &&
                              relationship.length <= 20
                            ) {
                              fields.push({
                                firstName,
                                ageRange,
                                relationship,
                              });
                              form.change('newMemberFirstName', '');
                              form.change('newMemberAgeRange', '');
                              form.change('newMemberRelationship', '');
                            }
                          }}
                        >
                          <svg
                            width="27"
                            height="27"
                            viewBox="0 0 27 27"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M13.5 0C6.04432 0 0 6.04432 0 13.5C0 20.9557 6.04432 27 13.5 27C20.9557 27 27 20.9557 27 13.5C27 6.04432 20.9557 0 13.5 0ZM14.7273 18.4091C14.7273 18.7346 14.598 19.0467 14.3678 19.2769C14.1377 19.5071 13.8255 19.6364 13.5 19.6364C13.1745 19.6364 12.8623 19.5071 12.6322 19.2769C12.402 19.0467 12.2727 18.7346 12.2727 18.4091V14.7273H8.59091C8.26542 14.7273 7.95325 14.598 7.7231 14.3678C7.49294 14.1377 7.36364 13.8255 7.36364 13.5C7.36364 13.1745 7.49294 12.8623 7.7231 12.6322C7.95325 12.402 8.26542 12.2727 8.59091 12.2727H12.2727V8.59091C12.2727 8.26542 12.402 7.95326 12.6322 7.7231C12.8623 7.49294 13.1745 7.36364 13.5 7.36364C13.8255 7.36364 14.1377 7.49294 14.3678 7.7231C14.598 7.95326 14.7273 8.26542 14.7273 8.59091V12.2727H18.4091C18.7346 12.2727 19.0467 12.402 19.2769 12.6322C19.5071 12.8623 19.6364 13.1745 19.6364 13.5C19.6364 13.8255 19.5071 14.1377 19.2769 14.3678C19.0467 14.598 18.7346 14.7273 18.4091 14.7273H14.7273V18.4091Z"
                              fill="#712739"
                            />
                          </svg>

                          <FormattedMessage id="ProfileSettingsForm.addButton" />
                        </Button>
                      </div>

                      {fields.length > 0 && (
                        <div className={css.addedMembersList}>
                          {fields.length > 1 && (
                            <p className={css.reorderHint}>
                              <FormattedMessage id="ProfileSettingsForm.reorderHint" />
                            </p>
                          )}
                          {fields.map((name, index) => {
                            const member = values?.householdMembers?.[index];
                            // The family member in the top position is the
                            // account owner; users set this by reordering.
                            const isAccountOwner = index === 0;
                            const isDragging =
                              this.state.memberDragIndex === index;
                            const isDropTarget =
                              this.state.memberDragIndex !== null &&
                              this.state.memberDragIndex !== index &&
                              this.state.memberDropIndex === index;

                            return (
                              <div
                                key={name}
                                ref={node => {
                                  this.memberNodes[index] = node;
                                }}
                                className={classNames(css.addedMemberItem, {
                                  [css.memberItemDragging]: isDragging,
                                  [css.memberItemDropTarget]: isDropTarget,
                                })}
                              >
                                <button
                                  type="button"
                                  className={css.dragHandle}
                                  ref={node => {
                                    this.memberHandles[index] = node;
                                  }}
                                  aria-label={intl.formatMessage(
                                    { id: 'ProfileSettingsForm.reorderMember' },
                                    {
                                      name: member?.firstName || '',
                                      position: index + 1,
                                      total: fields.length,
                                    }
                                  )}
                                  onPointerDown={e =>
                                    this.handleMemberDragStart(e, index)
                                  }
                                  onPointerMove={e =>
                                    this.handleMemberDragMove(e, fields.length)
                                  }
                                  onPointerUp={() =>
                                    this.handleMemberDragEnd(fields)
                                  }
                                  onPointerCancel={() =>
                                    this.handleMemberDragCancel()
                                  }
                                  onKeyDown={e =>
                                    this.handleMemberKeyDown(e, index, fields)
                                  }
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="10"
                                    height="16"
                                    viewBox="0 0 10 16"
                                    fill="none"
                                    aria-hidden="true"
                                    focusable="false"
                                  >
                                    <g fill="#8C8C8C">
                                      <circle cx="2" cy="3" r="1.5" />
                                      <circle cx="8" cy="3" r="1.5" />
                                      <circle cx="2" cy="8" r="1.5" />
                                      <circle cx="8" cy="8" r="1.5" />
                                      <circle cx="2" cy="13" r="1.5" />
                                      <circle cx="8" cy="13" r="1.5" />
                                    </g>
                                  </svg>
                                </button>
                                <span className={css.memberInfo}>
                                  {member?.firstName} | {member?.ageRange} |{' '}
                                  {member?.relationship}
                                </span>
                                {isAccountOwner && (
                                  <span className={css.accountOwnerTag}>
                                    <FormattedMessage id="ProfileSettingsForm.accountOwnerLabel" />
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className={css.removeButtonX}
                                  onClick={() => fields.remove(index)}
                                  aria-label={intl.formatMessage({
                                    id: 'ProfileSettingsForm.removeMember',
                                  })}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                  >
                                    <path
                                      d="M7 1L1 7M1 1L7 7"
                                      stroke="#363B3E"
                                      stroke-width="2"
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                    />
                                  </svg>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {fields.length >= MAX_MEMBERS && (
                        <p className={css.maxMembersWarning}>
                          <FormattedMessage id="ProfileSettingsForm.membersMaxReached" />
                        </p>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>
              {/* <div className={classNames(css.sectionContainer, css.lastSection)}>
                {userFieldProps.map(fieldProps => (
                  <CustomExtendedDataField {...fieldProps} formId={formId} />
                ))}
              </div> */}
              {submitError}
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={this.state.showSaveConfirmation}
              >
                <FormattedMessage id="ProfileSettingsForm.saveChanges" />
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
